import type { EditSelectOptionList } from 'components/forms/EditSelect';

import { dayOfWeekName, monthName, ordinalSuffix, ordinalWord, weekOfMonthName } from './scheduleLabels';

// Option lists for an opening rule's calendar fields. Plain constants rather than
// bespoke components: each is an `EditSelect` over a fixed list, and a component per
// list would add a file and a prop signature without adding behaviour.
//
// Every list starts with an empty option holding `null`, which is what "not narrowed
// by this field" means. Its key is '' so the browser's own `required` validation
// still treats it as no selection.

const NONE = { key: '', value: null, label: '—' } as const;

const range = (from: number, to: number): number[] => Array.from({ length: to - from + 1 }, (_, i) => from + i);

const capitalize = (s: string): string => s.charAt(0).toUpperCase() + s.slice(1);

export const MONTH_OPTIONS: EditSelectOptionList<number | null> = [NONE, ...range(1, 12).map((m) => ({ key: m, value: m, label: monthName(m) }))];

// 1–5, where 5 means "last" — the backend's own range for week_of_month.
export const WEEK_OF_MONTH_OPTIONS: EditSelectOptionList<number | null> = [
  NONE,
  ...range(1, 5).map((w) => ({ key: w, value: w, label: capitalize(weekOfMonthName(w)) })),
];

// ISO-8601 numbering: 1 = Monday … 7 = Sunday, matching the backend's 1–7 range.
export const DAY_OF_WEEK_OPTIONS: EditSelectOptionList<number | null> = [NONE, ...range(1, 7).map((d) => ({ key: d, value: d, label: dayOfWeekName(d) }))];

/**
 * Nth-of-month, counted from either end of the month.
 *
 * The backend's range is −30…31 with a sign convention rather than a separate
 * direction field: **positive counts from the start** of the month (1 = the 1st),
 * **0 means the last day**, and **negative counts back from the end** (−1 = the day
 * before last). The two group headings carry that, since the numbers alone do not
 * say which end they count from.
 */
export const NTH_OF_MONTH_OPTIONS: EditSelectOptionList<number | null> = [
  NONE,
  {
    key: 'from-start',
    label: 'Counting from the start of the month',
    options: range(1, 31).map((n) => ({ key: n, value: n, label: ordinalWord(n) })),
  },
  {
    key: 'from-end',
    label: 'Counting back from the end of the month',
    options: [{ key: 0, value: 0, label: 'Last day' }, ...range(1, 30).map((n) => ({ key: -n, value: -n, label: `${n}${ordinalSuffix(n)} from last` }))],
  },
];
